import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useSound } from '../hooks/useSound';
import { GameHeader, ScoreBadge } from '../lib/GameVisuals';
import { Bot, Pause, Play, SkipForward, ChevronRight, BookOpen, Gauge, Layers } from 'lucide-react';

/* ─── Content Type Labels ─── */
const CONTENT_TYPE_INFO = {
  vocabulary_theme: { label: 'Vocabulary', color: '#4C6EF5' },
  sentence_reading: { label: 'Sentences', color: '#0CA678' },
  phonics_pattern: { label: 'Phonics', color: '#7950F2' },
  minimal_pairs: { label: 'Minimal Pairs', color: '#F06595' },
  fluency_passage: { label: 'Fluency', color: '#E03131' },
  tongue_twister: { label: 'Tongue Twister', color: '#F59F00' },
  number_sequence: { label: 'Numbers', color: '#1971C2' },
  verb_conjugation: { label: 'Conjugation', color: '#E03131' },
  verb_conjugation_chain: { label: 'Verb Chain', color: '#E03131' },
  conjugation_chain: { label: 'Conj. Chain', color: '#E03131' },
};

/* ─── Tutorial Screen ─── */
function TutorialScreen({ onStart, items }) {
  const firstItem = items[0];
  const sampleTitle = firstItem?.title || firstItem?.title_en || 'Reading Practice';
  const sampleWords = (firstItem?.words || []).slice(0, 6);
  const contentTypes = [...new Set(items.map(i => i.content_type).filter(Boolean))];
  const typeLabels = contentTypes.slice(0, 4).map(ct => CONTENT_TYPE_INFO[ct]?.label || ct);

  return (
    <div data-testid="robot-balayeur-tutorial" style={{
      position: 'fixed', inset: 0, zIndex: 100,
      display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
      background: 'linear-gradient(180deg, #E1F0FA 0%, #D4EFFC 40%, #F0F4F8 100%)',
      padding: '2rem',
    }}>
      <div style={{ maxWidth: 760, textAlign: 'center' }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '0.75rem', marginBottom: '0.5rem' }}>
          <Bot size={48} color="#4C6EF5" />
          <h1 style={{ fontFamily: 'Fredoka', fontSize: '3.2rem', fontWeight: 700, color: '#1A1A2E' }}>
            ROBOT BALAYEUR
          </h1>
        </div>
        <p style={{ fontFamily: 'Nunito', fontSize: '1.5rem', color: '#333', marginBottom: '2rem', fontWeight: 600 }}>
          Read along as the robot highlights each word!
        </p>

        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '1rem', marginBottom: '2rem' }}>
          {/* Card 1: Read Along */}
          <div style={{
            background: 'rgba(255,255,255,0.92)', borderRadius: '1rem', padding: '1.25rem',
            border: '2px solid rgba(76,110,245,0.3)', boxShadow: '0 4px 16px rgba(0,0,0,0.08)',
          }}>
            <BookOpen size={40} color="#4C6EF5" style={{ marginBottom: '0.5rem' }} />
            <div style={{ fontFamily: 'Fredoka', fontSize: '1.3rem', fontWeight: 700, color: '#4C6EF5', marginBottom: '0.3rem' }}>
              Read Along
            </div>
            <div style={{ fontFamily: 'Nunito', fontSize: '1.05rem', color: '#555', lineHeight: 1.3 }}>
              The robot highlights each word — the whole class reads aloud together
            </div>
          </div>

          {/* Card 2: Speed Control */}
          <div style={{
            background: 'rgba(255,255,255,0.92)', borderRadius: '1rem', padding: '1.25rem',
            border: '2px solid rgba(245,159,0,0.3)', boxShadow: '0 4px 16px rgba(0,0,0,0.08)',
          }}>
            <Gauge size={40} color="#F59F00" style={{ marginBottom: '0.5rem' }} />
            <div style={{ fontFamily: 'Fredoka', fontSize: '1.3rem', fontWeight: 700, color: '#F59F00', marginBottom: '0.3rem' }}>
              Control Speed
            </div>
            <div style={{ fontFamily: 'Nunito', fontSize: '1.05rem', color: '#555', lineHeight: 1.3 }}>
              Use Slower/Faster buttons to match the class pace. Pause anytime!
            </div>
          </div>

          {/* Card 3: Sets */}
          <div style={{
            background: 'rgba(255,255,255,0.92)', borderRadius: '1rem', padding: '1.25rem',
            border: '2px solid rgba(12,166,120,0.3)', boxShadow: '0 4px 16px rgba(0,0,0,0.08)',
          }}>
            <Layers size={40} color="#0CA678" style={{ marginBottom: '0.5rem' }} />
            <div style={{ fontFamily: 'Fredoka', fontSize: '1.3rem', fontWeight: 700, color: '#0CA678', marginBottom: '0.3rem' }}>
              Reading Sets
            </div>
            <div style={{ fontFamily: 'Nunito', fontSize: '1.05rem', color: '#555', lineHeight: 1.3 }}>
              Multiple reading passages to practice — skip ahead or let them auto-advance
            </div>
          </div>
        </div>

        {/* Example passage */}
        <div style={{ marginBottom: '1.5rem' }}>
          <div style={{ fontFamily: 'Fredoka', fontSize: '1.05rem', color: '#555', marginBottom: '0.5rem' }}>
            First passage:
          </div>
          <div style={{
            display: 'inline-block', padding: '0.4rem 1.2rem', borderRadius: '0.75rem',
            background: 'rgba(255,255,255,0.9)', border: '2px solid rgba(76,110,245,0.15)',
            fontFamily: 'Fredoka', fontSize: '1.15rem', fontWeight: 600, color: '#333', marginBottom: '0.75rem',
          }}>
            {sampleTitle}
          </div>
          {sampleWords.length > 0 && (
            <div style={{ display: 'flex', gap: '0.4rem', justifyContent: 'center', flexWrap: 'wrap', marginTop: '0.5rem' }}>
              {sampleWords.map((w, i) => (
                <span key={i} style={{
                  padding: '0.25rem 0.7rem', borderRadius: '0.5rem',
                  background: i === 0 ? 'rgba(76,110,245,0.12)' : 'transparent',
                  border: i === 0 ? '2px solid rgba(76,110,245,0.3)' : '2px solid transparent',
                  fontFamily: 'Fredoka', fontSize: '1.1rem', fontWeight: 600,
                  color: i === 0 ? '#4C6EF5' : '#888',
                }}>
                  {w}
                </span>
              ))}
              {(firstItem?.words?.length || 0) > 6 && (
                <span style={{ fontFamily: 'Nunito', fontSize: '1rem', color: '#aaa', fontWeight: 600 }}>
                  ...+{firstItem.words.length - 6} more
                </span>
              )}
            </div>
          )}
          {typeLabels.length > 0 && (
            <div style={{ marginTop: '0.75rem', display: 'flex', gap: '0.4rem', justifyContent: 'center', flexWrap: 'wrap' }}>
              {typeLabels.map((label, i) => (
                <span key={i} style={{
                  padding: '0.15rem 0.6rem', borderRadius: '2rem',
                  background: 'rgba(76,110,245,0.08)', fontFamily: 'Fredoka',
                  fontSize: '0.85rem', fontWeight: 600, color: '#4C6EF5',
                }}>
                  {label}
                </span>
              ))}
            </div>
          )}
        </div>

        <button data-testid="robot-tutorial-start" onClick={onStart} className="btn-arcade btn-primary"
          style={{ fontSize: '1.8rem', padding: '0 3rem', minHeight: 72, gap: '0.75rem', borderBottomWidth: 8 }}>
          <ChevronRight size={32} /> Start Reading!
        </button>
      </div>
    </div>
  );
}

/* ─── Set Transition Screen ─── */
function SetTransition({ setNumber, totalSets, title, titleEn, contentType, wordCount, onReady }) {
  const [countdown, setCountdown] = useState(3);
  const onReadyRef = useRef(onReady);
  onReadyRef.current = onReady;

  useEffect(() => {
    const interval = setInterval(() => {
      setCountdown(c => {
        if (c <= 1) {
          clearInterval(interval);
          setTimeout(() => onReadyRef.current(), 0);
          return 0;
        }
        return c - 1;
      });
    }, 1000);
    return () => clearInterval(interval);
  }, []);

  const typeInfo = CONTENT_TYPE_INFO[contentType] || { label: 'Reading', color: '#4C6EF5' };

  return (
    <div data-testid="robot-set-transition" style={{
      position: 'absolute', inset: 0, zIndex: 50,
      display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
      background: 'rgba(0,0,0,0.55)', backdropFilter: 'blur(8px)',
    }}>
      <div style={{
        background: 'rgba(255,255,255,0.95)', borderRadius: '1.5rem', padding: '2rem 3rem',
        textAlign: 'center', boxShadow: '0 8px 32px rgba(0,0,0,0.2)',
        animation: 'popIn 0.3s ease-out', maxWidth: 600,
      }}>
        <style>{`@keyframes popIn { from { transform: scale(0.8); opacity: 0; } to { transform: scale(1); opacity: 1; } }`}</style>
        <div style={{ fontFamily: 'Fredoka', fontSize: '1.2rem', color: 'var(--text-muted)', fontWeight: 600, marginBottom: '0.3rem' }}>
          PASSAGE {setNumber} of {totalSets}
        </div>
        <span style={{
          display: 'inline-block', padding: '0.2rem 0.75rem', borderRadius: '2rem',
          background: `${typeInfo.color}12`, fontFamily: 'Fredoka', fontSize: '0.9rem',
          fontWeight: 700, color: typeInfo.color, marginBottom: '0.5rem',
        }}>
          {typeInfo.label}
        </span>
        <div style={{
          fontFamily: 'Fredoka', fontSize: '1.6rem', fontWeight: 700, color: '#1A1A2E', marginBottom: '0.3rem',
        }}>
          {title || titleEn || 'Reading Practice'}
        </div>
        {titleEn && title && (
          <div style={{ fontFamily: 'Nunito', fontSize: '1rem', color: '#888', fontWeight: 600, marginBottom: '0.3rem' }}>
            {titleEn}
          </div>
        )}
        <span style={{
          display: 'inline-block', padding: '0.2rem 0.6rem', borderRadius: '2rem',
          background: 'rgba(76,110,245,0.08)', fontFamily: 'Fredoka', fontSize: '0.9rem',
          fontWeight: 700, color: '#4C6EF5',
        }}>
          {wordCount} words
        </span>
        <div style={{ marginTop: '1rem', fontFamily: 'Fredoka', fontSize: '2.5rem', fontWeight: 700, color: 'var(--primary)' }}>
          {countdown}
        </div>
      </div>
    </div>
  );
}

export function RobotBalayeur({ items, settings, onGameEnd }) {
  const { playSound, playGameMusic, stopMusic } = useSound();
  useEffect(() => { playGameMusic('robot_balayeur'); return () => stopMusic(); }, [playGameMusic, stopMusic]);
  const robotItems = items.filter(i => i.words && i.words.length > 0);

  const [showTutorial, setShowTutorial] = useState(true);
  const [showTransition, setShowTransition] = useState(false);
  const [setIdx, setSetIdx] = useState(0);
  const [repeatNum, setRepeatNum] = useState(0); // 0-indexed: which repeat of the current passage
  const [playbackRate, setPlaybackRate] = useState(1.0);
  const [paused, setPaused] = useState(false);
  const [robotPos, setRobotPos] = useState(-1);
  const [readWords, setReadWords] = useState(new Set());
  const containerRef = useRef(null);
  const audioRef = useRef(null);
  const rafRef = useRef(null);
  const endedRef = useRef(false);
  const setIdxRef = useRef(0);
  const repeatNumRef = useRef(0);
  const reviewRef = useRef([]);
  const pausedRef = useRef(false);
  setIdxRef.current = setIdx;
  repeatNumRef.current = repeatNum;
  pausedRef.current = paused;

  const maxRepeats = settings.passageRepeat || 2;

  const currentSet = robotItems[setIdx];
  const words = currentSet?.words || [];
  const timestamps = currentSet?.word_timestamps || [];
  const rows = currentSet?.rows || 3;
  const wordRows = [];
  const perRow = Math.ceil(words.length / rows);
  for (let i = 0; i < rows; i++) wordRows.push(words.slice(i * perRow, (i + 1) * perRow));

  const totalSets = Math.min(settings.rounds || 999, robotItems.length);

  const handleTutorialDone = useCallback(() => {
    setShowTutorial(false);
    setShowTransition(true);
  }, []);

  const handleTransitionDone = useCallback(() => {
    setShowTransition(false);
  }, []);

  // Advance to next passage or end game
  const advanceSet = useCallback(() => {
    const idx = setIdxRef.current;
    const passage = robotItems[idx];
    if (passage) {
      reviewRef.current.push({
        title: passage.title || passage.title_en || 'Passage',
        contentType: CONTENT_TYPE_INFO[passage.content_type]?.label || '',
        words: [...(passage.words || [])],
      });
    }
    const next = idx + 1;
    if (next >= totalSets || next >= robotItems.length) {
      if (!endedRef.current) {
        endedRef.current = true;
        playSound('win');
        for (let i = reviewRef.current.length; i < totalSets && i < robotItems.length; i++) {
          const p = robotItems[i]; if (!p) break;
          reviewRef.current.push({
            title: p.title || p.title_en || 'Passage',
            contentType: CONTENT_TYPE_INFO[p.content_type]?.label || '',
            words: [...(p.words || [])],
          });
        }
        setTimeout(() => onGameEnd({
          team1Score: idx + 1, team2Score: 0,
          totalQuestions: idx + 1, accuracy: 100, bestStreak: idx + 1,
          questionReview: reviewRef.current, reviewType: 'passage',
        }), 1000);
      }
    } else {
      setSetIdx(next);
      setShowTransition(true);
    }
  }, [totalSets, robotItems, playSound, onGameEnd]);

  // Skip-to-end: parent fires this when teacher hits "Skip" in the game menu.
  // Reports the number of passages already covered as the score.
  useEffect(() => {
    const onForceEnd = () => {
      if (endedRef.current) return;
      endedRef.current = true;
      playSound('win');
      const passagesDone = setIdxRef.current; // already-completed sets
      // Snapshot what's been reviewed plus the current in-progress one
      const review = [...reviewRef.current];
      const cur = robotItems[setIdxRef.current];
      if (cur && review.length <= setIdxRef.current) {
        review.push({
          title: cur.title || cur.title_en || 'Passage',
          contentType: CONTENT_TYPE_INFO[cur.content_type]?.label || '',
          words: [...(cur.words || [])],
        });
      }
      setTimeout(() => onGameEnd({
        team1Score: passagesDone, team2Score: 0,
        totalQuestions: passagesDone, accuracy: passagesDone > 0 ? 100 : 0,
        bestStreak: passagesDone,
        questionReview: review, reviewType: 'passage',
      }), 600);
    };
    window.addEventListener('frenchboard:end-game-now', onForceEnd);
    return () => window.removeEventListener('frenchboard:end-game-now', onForceEnd);
  }, [robotItems, onGameEnd, playSound]);

  // Audio-driven highlight tracking loop
  const trackAudio = useCallback(() => {
    const audio = audioRef.current;
    if (!audio || pausedRef.current) {
      rafRef.current = requestAnimationFrame(trackAudio);
      return;
    }

    const passage = robotItems[setIdxRef.current];
    const ts = passage?.word_timestamps || [];
    if (ts.length === 0) {
      rafRef.current = requestAnimationFrame(trackAudio);
      return;
    }

    const t = audio.currentTime;
    let activeIdx = -1;

    // Find which word the audio is currently on
    for (let i = 0; i < ts.length; i++) {
      // A word is active from its start until the next word starts (or a small buffer after end)
      const nextStart = i + 1 < ts.length ? ts[i + 1].start : ts[i].end + 1.0;
      if (t >= ts[i].start && t < nextStart) {
        activeIdx = i;
        break;
      }
    }

    // Update highlight position
    setRobotPos(prev => {
      if (prev !== activeIdx) {
        // Mark all words before the active one as read
        if (activeIdx > 0) {
          setReadWords(old => {
            const next = new Set(old);
            for (let j = 0; j < activeIdx; j++) next.add(j);
            return next;
          });
        }
        return activeIdx;
      }
      return prev;
    });

    rafRef.current = requestAnimationFrame(trackAudio);
  }, [robotItems]);

  // Stop audio and tracking
  const stopPassage = useCallback(() => {
    if (rafRef.current) cancelAnimationFrame(rafRef.current);
    rafRef.current = null;
    if (audioRef.current) {
      audioRef.current.pause();
      audioRef.current.removeAttribute('src');
      audioRef.current = null;
    }
  }, []);

  // Start passage: create audio once, handle repeats by seeking to 0
  const startPassage = useCallback((passage) => {
    if (!passage?.item_id) return;
    stopPassage();

    repeatNumRef.current = 0;
    setRepeatNum(0);
    setRobotPos(-1);
    setReadWords(new Set());
    setPaused(false);

    const AUDIO_BASE = process.env.REACT_APP_AUDIO_BASE_URL;
    if (!AUDIO_BASE) return;

    const url = `${AUDIO_BASE}/robot_balayeur/${passage.item_id}.mp3`;
    const audio = new Audio(url);
    audio.playbackRate = playbackRate;
    audioRef.current = audio;

    audio.addEventListener('ended', () => {
      // Mark all words as read
      setReadWords(new Set((passage.words || []).map((_, i) => i)));
      setRobotPos(passage.words ? passage.words.length - 1 : -1);

      const currentRepeat = repeatNumRef.current;
      if (currentRepeat + 1 < maxRepeats) {
        // More repeats: seek to beginning and replay (same audio element — no loading gap)
        const nextRepeat = currentRepeat + 1;
        repeatNumRef.current = nextRepeat;
        setRepeatNum(nextRepeat);
        setTimeout(() => {
          setRobotPos(-1);
          setReadWords(new Set());
          audio.currentTime = 0;
          audio.play().catch(() => {});
        }, 1000);
      } else {
        // All repeats done — advance to next passage
        setTimeout(() => advanceSet(), 800);
      }
    });

    audio.play().catch(() => {});
    // Start tracking loop (runs continuously through all repeats)
    rafRef.current = requestAnimationFrame(trackAudio);
  }, [playbackRate, stopPassage, trackAudio, advanceSet, maxRepeats]);

  // Start when transition ends
  useEffect(() => {
    if (showTutorial || showTransition || !currentSet || !containerRef.current) return;
    setRobotPos(-1);
    setReadWords(new Set());
    setPaused(false);
    startPassage(currentSet);
    return () => stopPassage();
  }, [setIdx, showTransition, showTutorial]);

  const togglePause = () => {
    const audio = audioRef.current;
    if (!audio) return;
    if (paused) {
      audio.play().catch(() => {});
    } else {
      audio.pause();
    }
    setPaused(!paused);
    playSound('click');
  };

  const isLastSet = setIdx + 1 >= totalSets || setIdx + 1 >= robotItems.length;

  const nextSet = () => {
    stopPassage();
    setRepeatNum(0);
    repeatNumRef.current = 0;
    if (isLastSet) {
      advanceSet();
    } else {
      setSetIdx(setIdx + 1);
      setShowTransition(true);
    }
    playSound('click');
  };

  if (!currentSet && !showTutorial) {
    return (
      <div style={{
        height: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center',
        background: 'var(--bg)', color: 'var(--text-muted)', fontSize: '1.5rem', fontFamily: 'Fredoka',
      }}>
        No reading content available
      </div>
    );
  }

  const progress = totalSets > 0 ? (setIdx / totalSets) * 100 : 0;
  const wordProgress = words.length > 0 && robotPos >= 0 ? ((robotPos + 1) / words.length) * 100 : 0;
  const typeInfo = CONTENT_TYPE_INFO[currentSet?.content_type] || { label: 'Reading', color: '#4C6EF5' };

  return (
    <div data-testid="robot-balayeur-game" style={{
      height: '100vh', display: 'flex', flexDirection: 'column',
      background: 'linear-gradient(180deg, #E1F0FA 0%, #F0F4F8 100%)',
      position: 'relative',
    }}>
      {showTutorial && <TutorialScreen onStart={handleTutorialDone} items={robotItems.slice(0, totalSets)} />}

      {showTransition && currentSet && (
        <SetTransition
          setNumber={setIdx + 1}
          totalSets={totalSets}
          title={currentSet.title}
          titleEn={currentSet.title_en}
          contentType={currentSet.content_type}
          wordCount={words.length}
          onReady={handleTransitionDone}
        />
      )}

      <GameHeader title="ROBOT BALAYEUR" icon={<Bot size={24} color="var(--primary)" />}>
        {/* French title + content type badge */}
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
          <span style={{
            padding: '0.15rem 0.5rem', borderRadius: '2rem',
            background: `${typeInfo.color}12`, fontFamily: 'Fredoka',
            fontSize: '0.9rem', fontWeight: 700, color: typeInfo.color,
          }}>
            {typeInfo.label}
          </span>
          <span style={{ fontFamily: 'Fredoka', fontSize: '0.9rem', color: 'var(--text-primary)', fontWeight: 600, maxWidth: 300, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
            {currentSet?.title || currentSet?.title_en || ''}
          </span>
        </div>
        <ScoreBadge label="PASSAGE" value={`${setIdx + 1}/${totalSets}`} color="var(--primary)" />
        {maxRepeats > 1 && (
          <div data-testid="repeat-badge" style={{
            display: 'flex', alignItems: 'center', gap: '0.3rem',
            background: 'rgba(12,166,120,0.08)', padding: '0.2rem 0.6rem', borderRadius: '1rem',
            border: '1.5px solid rgba(12,166,120,0.2)',
          }}>
            <span style={{ fontFamily: 'Fredoka', fontSize: '0.9rem', fontWeight: 700, color: '#0CA678' }}>
              Repeat {repeatNum + 1} of {maxRepeats}
            </span>
          </div>
        )}
        <div style={{
          display: 'flex', alignItems: 'center', gap: '0.4rem',
          background: 'rgba(76,110,245,0.06)', padding: '0.2rem 0.6rem', borderRadius: '1rem',
        }}>
          <Gauge size={14} color="var(--text-muted)" />
          <span className="font-score" style={{ fontSize: '1.1rem', fontWeight: 800, color: 'var(--accent)' }}>{playbackRate.toFixed(2)}x</span>
        </div>
      </GameHeader>

      {/* Progress bars */}
      <div style={{ height: 4, background: 'rgba(0,0,0,0.04)', position: 'relative' }}>
        {/* Overall set progress (thin line) */}
        <div style={{
          height: '100%', width: `${progress}%`, background: 'var(--primary)',
          transition: 'width 0.5s ease', borderRadius: '0 2px 2px 0',
        }} />
      </div>
      {/* Word progress bar within current set */}
      <div style={{ height: 3, background: 'rgba(0,0,0,0.02)' }}>
        <div style={{
          height: '100%', width: `${wordProgress}%`,
          background: 'linear-gradient(90deg, #4C6EF5, #748FFC)',
          transition: 'width 0.3s ease', borderRadius: '0 2px 2px 0',
        }} />
      </div>

      {/* Words display area */}
      {currentSet && (
        <div ref={containerRef} style={{
          flex: 1, display: 'flex', flexDirection: 'column', justifyContent: 'center',
          padding: '1.5rem 3rem', gap: '1.25rem',
        }}>
          {wordRows.map((row, rIdx) => (
            <div key={rIdx} style={{ display: 'flex', flexWrap: 'wrap', gap: '0.75rem', justifyContent: 'center' }}>
              {row.map((word, wIdx) => {
                const gIdx = rIdx * perRow + wIdx;
                const active = gIdx === robotPos;
                const read = readWords.has(gIdx);
                return (
                  <span key={wIdx} data-word={gIdx} data-testid={`robot-word-${gIdx}`} style={{
                    fontSize: 'clamp(1.5rem, 3vw, 2.75rem)', fontWeight: 700, fontFamily: 'Fredoka',
                    color: active ? '#4C6EF5' : read ? 'var(--text-secondary)' : 'var(--text-primary)',
                    padding: '0.4rem 1rem', borderRadius: '0.75rem',
                    background: active ? 'rgba(76,110,245,0.12)' : 'transparent',
                    border: active ? '2.5px solid rgba(76,110,245,0.4)' : '2.5px solid transparent',
                    boxShadow: active ? '0 2px 12px rgba(76,110,245,0.15)' : 'none',
                    transition: 'all 0.25s ease',
                    transform: active ? 'scale(1.08)' : 'scale(1)',
                    textDecoration: read && !active ? 'underline' : 'none',
                    textDecorationColor: read && !active ? 'rgba(76,110,245,0.25)' : 'transparent',
                    textUnderlineOffset: '4px',
                    opacity: 1, // Keep words fully visible always
                  }}>
                    {word}
                  </span>
                );
              })}
            </div>
          ))}
        </div>
      )}

      {/* Control bar */}
      <div style={{
        padding: '0.75rem 1.5rem', display: 'flex', justifyContent: 'center', alignItems: 'center', gap: '1rem',
        background: 'rgba(255,255,255,0.95)',
        borderTop: '2px solid rgba(0,0,0,0.06)',
        boxShadow: '0 -2px 12px rgba(0,0,0,0.04)',
      }}>
        <ControlButton onClick={togglePause} icon={paused ? <Play size={20} /> : <Pause size={20} />} label={paused ? 'Play' : 'Pause'} primary />

        {/* Speed slider */}
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', minWidth: 280 }}>
          <span style={{ fontFamily: 'Fredoka', fontSize: '0.8rem', fontWeight: 700, color: '#2F9E44', whiteSpace: 'nowrap' }}>Slow</span>
          <input
            data-testid="speed-slider"
            type="range" min="0.50" max="2.00" step="0.05"
            value={playbackRate}
            onChange={(e) => {
              const r = parseFloat(e.target.value);
              setPlaybackRate(r);
              if (audioRef.current) audioRef.current.playbackRate = r;
            }}
            style={{
              flex: 1, height: 6, appearance: 'none', WebkitAppearance: 'none',
              background: `linear-gradient(to right, #2F9E44 0%, #4C6EF5 50%, #E03131 100%)`,
              borderRadius: 3, outline: 'none', cursor: 'pointer',
            }}
          />
          <span style={{ fontFamily: 'Fredoka', fontSize: '0.8rem', fontWeight: 700, color: '#E03131', whiteSpace: 'nowrap' }}>Fast</span>
          <span style={{
            fontFamily: 'Fredoka', fontSize: '1rem', fontWeight: 800,
            color: 'var(--primary)', minWidth: 48, textAlign: 'center',
          }}>{playbackRate.toFixed(2)}x</span>
        </div>
        <style>{`
          input[data-testid="speed-slider"]::-webkit-slider-thumb {
            -webkit-appearance: none; appearance: none;
            width: 24px; height: 24px; border-radius: 50%;
            background: white; border: 3px solid var(--primary);
            box-shadow: 0 2px 6px rgba(0,0,0,0.2);
            cursor: pointer;
          }
          input[data-testid="speed-slider"]::-moz-range-thumb {
            width: 24px; height: 24px; border-radius: 50%;
            background: white; border: 3px solid var(--primary);
            box-shadow: 0 2px 6px rgba(0,0,0,0.2);
            cursor: pointer;
          }
        `}</style>

        <ControlButton onClick={nextSet} icon={<SkipForward size={20} />} label={isLastSet ? 'End' : 'Next'} accent />
      </div>
    </div>
  );
}

function ControlButton({ onClick, icon, label, primary, accent }) {
  const bg = primary ? 'var(--primary)' : accent ? 'var(--accent)' : 'white';
  const color = primary || accent ? 'white' : 'var(--text-primary)';
  const borderC = primary ? '#3B5BDB' : accent ? '#E67700' : 'var(--border)';
  return (
    <button data-testid={`control-${label.toLowerCase()}`} onClick={onClick} style={{
      minHeight: 52, padding: '0 1.5rem', borderRadius: '1rem',
      background: bg, color, border: 'none', borderBottom: `4px solid ${borderC}`,
      fontSize: '1.1rem', fontWeight: 700, fontFamily: 'Fredoka',
      cursor: 'pointer', touchAction: 'manipulation',
      display: 'flex', alignItems: 'center', gap: '0.5rem',
      boxShadow: `0 2px 8px ${primary ? 'rgba(76,110,245,0.2)' : accent ? 'rgba(245,159,0,0.2)' : 'rgba(0,0,0,0.05)'}`,
      transition: 'transform 0.1s',
    }}
      onMouseDown={e => e.currentTarget.style.transform = 'translateY(3px)'}
      onMouseUp={e => e.currentTarget.style.transform = ''}
    >
      {icon} {label}
    </button>
  );
}
